import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { AnalyzeRequest } from '@fishymeter/types';
import { api, uploadFileToS3 } from '@/lib/api';

export function useSubmitAnalysis() {
  return useMutation({
    mutationFn: (input: AnalyzeRequest) => api.submitAnalysis(input),
  });
}

export type FileSubmitPhase = 'idle' | 'submitting' | 'uploading';

export interface FileSubmitInput {
  kind: 'image' | 'pdf';
  file: File;
}

/**
 * File submit is three steps, still surfaced to the user as the same two
 * visible phases:
 *   1. POST /api/analyze/upload-url  -> file_id + presigned PUT  ("submitting")
 *   2. PUT the file to S3                                        ("uploading")
 *   3. POST /api/analyze with the file_id -> analysis_id
 *
 * The order changed with Requirement 5: the upload now completes BEFORE the
 * analysis is created, so the state machine has a single entry point and an
 * abandoned upload leaves no pending analysis behind. Step 3 is what starts
 * the workflow; the caller then polls /api/analyze/{id}/status as before.
 */
export function useSubmitFileAnalysis() {
  const [phase, setPhase] = useState<FileSubmitPhase>('idle');

  const mutation = useMutation({
    mutationFn: async ({ kind, file }: FileSubmitInput): Promise<{ analysis_id: string }> => {
      setPhase('submitting');
      const slot = await api.requestUploadUrl({
        input_type: kind,
        filename: file.name,
        content_type: file.type,
        size: file.size,
      });

      setPhase('uploading');
      await uploadFileToS3(slot.upload_url, file);

      // Only now does an analysis exist. Quota is consumed here, not above.
      const submitted = await api.submitAnalysis({
        input_type: kind,
        file_id: slot.file_id,
        content_type: file.type,
        filename: file.name,
      });

      setPhase('idle');
      return { analysis_id: submitted.analysis_id };
    },
    onError: () => setPhase('idle'),
  });

  return { ...mutation, phase };
}

export function useAnalysisStatus(id: string | null) {
  return useQuery({
    queryKey: ['analysis', id, 'status'],
    queryFn: () => api.getStatus(id!),
    enabled: !!id,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'complete' || status === 'failed' ? false : 5000;
    },
  });
}
